<script setup lang="ts">
import DateChip from 'shared/components/DateChip/DateChip.vue';
import ScoreChip from 'shared/components/ScoreChip/ScoreChip.vue';
import TimerChip from 'shared/components/TimerChip/TimerChip.vue';
import type { ListItemStatus } from 'shared/composables/useListFilters/useListFilters';
import { mdiBookOpenVariant, mdiLockOutline } from '@quasar/extras/mdi-v7';
import { formatTime } from 'shared/composables/useTimer/useTimer';
import { useDate } from 'src/shared/composables/useDate';
import { normalizePersistedAccumulatorMs } from 'src/shared/utilities/timeAccumulator';

const { strapiDate } = useDate();

interface Props {
  sectionName: string;
  subtitle?: string | null;
  pausedMessage?: string | null;
  /** Shown when the row is student-locked but the viewer may enter (teacher preview). */
  lockedHint?: string | null;
  startDate?: string | null;
  score?: number | null;
  documentId: string;
  timeAccumulator?: number;
  disable?: boolean;
  active?: boolean;
  status?: ListItemStatus | null;
}

defineProps<Props>();
defineEmits<{
  'section:selected': [];
}>();

// Status is painted onto the row (left edge + avatar tint) instead of adding
// a fourth chip to the meta cluster; the tooltip keeps it explicit.
const STATUS_LABELS: Record<ListItemStatus, string> = {
  completed: 'COMPLETED',
  in_progress: 'IN_PROGRESS',
  not_started: 'NOT_STARTED',
};
</script>

<template>
  <q-item
    v-if="documentId"
    clickable
    class="section-row q-pa-sm"
    :disable="disable"
    :active="active"
    :class="[
      { 'section-row--active': active },
      status ? `section-row--status-${status}` : '',
    ]"
    @click="$emit('section:selected')"
  >
    <q-item-section
      avatar
      class="section-row__avatar"
    >
      <q-avatar class="section-row__avatar-circle">
        <q-icon
          :name="mdiBookOpenVariant"
          size="18px"
        />
      </q-avatar>
    </q-item-section>

    <q-item-section class="section-row__content">
      <q-item-label
        class="section-name text-brand"
        :class="{ 'text-bold': active, 'text-h6': !$q.platform.is.mobile }"
      >
        {{ sectionName }}
      </q-item-label>
      <q-item-label
        caption
        class="section-subtitle"
      >
        {{ subtitle }}
      </q-item-label>

      <q-item-label
        v-if="status"
        caption
        class="section-status-line q-mt-xs"
        :class="`section-status-line--${status}`"
      >
        <span
          class="section-status-line__dot"
          :class="`section-status-line__dot--${status}`"
        />
        {{ $t(STATUS_LABELS[status]) }}
      </q-item-label>

      <div
        v-if="!$q.screen.gt.xs"
        class="row items-center q-col-gutter-xs q-row-gutter-xs q-mt-sm"
      >
        <div>
          <DateChip
            class="chip-soft"
            color="accent"
          >
            {{ startDate ? strapiDate(startDate, 'short') : $t('NOT_STARTED') }}
          </DateChip>
        </div>
        <div>
          <ScoreChip
            v-if="score !== null && score !== undefined"
            class="section-score-chip"
            color="secondary"
            >{{ score }}</ScoreChip
          >
        </div>
        <div>
          <TimerChip
            class="chip-soft"
            color="secondary"
            >{{
              formatTime(
                normalizePersistedAccumulatorMs(timeAccumulator),
                'min',
              )
            }}</TimerChip
          >
        </div>
      </div>

      <q-item-label
        v-if="pausedMessage"
        caption
        class="text-warning q-mt-xs"
      >
        {{ pausedMessage }}
      </q-item-label>

      <q-item-label
        v-if="lockedHint"
        caption
        class="text-warning q-mt-xs"
      >
        <q-icon
          :name="mdiLockOutline"
          size="14px"
          class="q-mr-xs"
        />{{ lockedHint }}
      </q-item-label>
    </q-item-section>

    <q-item-section
      v-if="$q.screen.gt.xs"
      side
      class="section-row__meta"
    >
      <q-item-label class="row justify-end">
        <DateChip
          class="chip-soft"
          color="accent"
        >
          {{ startDate ? strapiDate(startDate, 'short') : $t('NOT_STARTED') }}
        </DateChip>
      </q-item-label>
      <q-item-label class="row items-center justify-end q-gutter-xs q-mt-xs">
        <ScoreChip
          v-if="score !== null && score !== undefined"
          class="section-score-chip"
          color="secondary"
          >{{ score }}</ScoreChip
        >
        <TimerChip
          class="chip-soft"
          color="secondary"
          >{{
            formatTime(normalizePersistedAccumulatorMs(timeAccumulator), 'min')
          }}</TimerChip
        >
      </q-item-label>
    </q-item-section>
  </q-item>
</template>

<style scoped>
.section-row {
  border: 1px solid rgba(52, 84, 122, 0.16);
  border-radius: 14px;
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 176, 155, 0.08),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98),
      rgba(246, 250, 255, 0.96)
    );
  transition:
    box-shadow 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.section-row:hover {
  border-color: rgba(0, 151, 167, 0.35);
  box-shadow: 0 2px 8px rgba(18, 62, 98, 0.08);
  transform: translateY(-1px);
}

/*
 * Dark mode: same shape and same teal top-right accent, but painted on the
 * standard dark card surface instead of white. Without this the row stayed a
 * white slab on a #121212 page, and `:disable` (opacity .6) turned it to mud.
 */
.body--dark .section-row {
  border-color: var(--border-subtle);
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 176, 155, 0.14),
      transparent 55%
    ),
    linear-gradient(180deg, #232323, var(--surface-card));
}

.body--dark .section-row:hover {
  border-color: rgba(0, 176, 155, 0.4);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

/*
 * Kept tight and near-vertically-centred: the previous `0 8px 20px` cast a dark
 * halo down into the gap and under the following row.
 */
.section-row--active {
  border-color: rgba(0, 151, 167, 0.5);
  box-shadow: 0 1px 6px rgba(18, 62, 98, 0.08);
}

.body--dark .section-row--active {
  border-color: rgba(0, 176, 155, 0.55);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
}

/*
 * Status is a quiet accent, not another chip: a colored left edge plus a
 * matching avatar tint. Same vocabulary as the filter bar's status filter —
 * green = completed, amber = in progress, grey = not started.
 */
.section-row--status-completed {
  border-left: 4px solid #21ba45;
}

.section-row--status-completed .section-row__avatar-circle {
  background: rgba(33, 186, 69, 0.16);
  color: #157a30;
}

.section-row--status-in_progress {
  border-left: 4px solid #f2c037;
}

.section-row--status-in_progress .section-row__avatar-circle {
  background: rgba(242, 192, 55, 0.22);
  color: #8a6d1a;
}

.section-row--status-not_started {
  border-left: 4px solid rgba(120, 130, 140, 0.45);
}

/* Textual status under the subtitle: colored bullet + plain caption text. */
.section-status-line {
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-status-line__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.section-status-line__dot--completed {
  background: #21ba45;
}

.section-status-line__dot--in_progress {
  background: #f2c037;
}

.section-status-line__dot--not_started {
  background: rgba(120, 130, 140, 0.65);
}

/* Text takes the status color too — darker shades in light mode for contrast,
 * lighter ones in dark mode. */
.section-status-line--completed {
  color: #157a30;
}

.section-status-line--in_progress {
  color: #8a6d1a;
}

.section-status-line--not_started {
  color: rgba(96, 106, 116, 0.9);
}

.body--dark .section-status-line--completed {
  color: #7fdc9c;
}

.body--dark .section-status-line--in_progress {
  color: #ffd977;
}

.body--dark .section-status-line--not_started {
  color: rgba(180, 188, 196, 0.85);
}

.body--dark .section-row--status-completed .section-row__avatar-circle {
  background: rgba(33, 186, 69, 0.28);
  color: #7fdc9c;
}

.body--dark .section-row--status-in_progress .section-row__avatar-circle {
  background: rgba(242, 192, 55, 0.26);
  color: #ffd977;
}

.section-row__avatar {
  min-width: 44px;
  padding-right: 6px;
}

.section-row__avatar-circle {
  background: linear-gradient(
    145deg,
    rgba(0, 151, 167, 0.16),
    rgba(29, 194, 58, 0.18)
  );
  color: #19647e;
}

.body--dark .section-row__avatar-circle {
  background: linear-gradient(
    145deg,
    rgba(0, 151, 167, 0.34),
    rgba(29, 194, 58, 0.34)
  );
  color: #7fdcd0;
}

.section-row__content {
  min-width: 0;
}

.section-name {
  white-space: normal;
  line-height: 1.3;
  letter-spacing: 0.01em;
}

.section-subtitle {
  color: rgba(35, 47, 62, 0.68);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.body--dark .section-subtitle {
  color: var(--text-muted);
}

.section-row__meta {
  min-width: 170px;
}

.chip-soft {
  box-shadow: inset 0 0 0 1px rgba(26, 66, 108, 0.12);
}

.body--dark .chip-soft {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.14);
}

.section-score-chip {
  background: linear-gradient(120deg, #ffdb4d, #ffc107);
  color: #3d2a00;
  font-weight: 700;
}
</style>
